// Unfortunately, import order matters here due to
// all sorts of stuff being set and depended upon in global scope.
import 'regenerator-runtime/runtime';
import React from 'react';
import ReactDOM from 'react-dom';

import $ from 'jquery';

declare global {
  interface Window {
    jQuery: typeof $;
    $: typeof $;
  }
}

window.jQuery = window.$ = $;

import { initializeSentry } from 'js/utils/initializeSentry';

initializeSentry();

import 'js/single_work/_namespace';
import 'js/single_work/preview_controller';
import { watchDomImpressions } from 'js/utils/impressions/watchDomImpressions';
import routes from 'js/core_pre_webpack/routes';
import { set_gmtoffset } from 'js/core_webpack/metrics/gmt_offset';
import ConfirmationBanner from 'js/single_work/loswp/ConfirmationBanner';
import { loadRecordHit } from 'js/utils/loadRecordHit';
import { currentUser } from 'js/utils/user';
import { startActivityTracking } from 'js/core_webpack/metrics/page_activity_tracking';
import { setUpHeaders } from 'js/single_work/loswp/setUpHeaders';
import { setUpLoginButtons } from 'js/simple_layout/header/setUpHeader';
import setUpScribdDocument from 'js/single_work/loswp/setUpScribdDocument';
import setUpClickTracking from 'js/single_work/loswp/setUpClickTracking';
import { setUpFigureCarousel } from 'js/single_work/loswp/setUpFigureCarousel';
import {
  setUpRelatedPapersCarousel,
  setUpRelatedPapersCarouselCardClick,
} from 'js/single_work/loswp/setUpRelatedPapersCarousel';
import setUpViewMoreRelatedWorks from 'js/single_work/loswp/setUpViewMoreRelatedWorks';
import { setUpTruncateComponent } from 'js/single_work/loswp/setUpTruncateComponent';
import { MoreActionsMenu } from 'js/single_work/loswp/MoreActionsMenu';
import registerForRails from 'js/utils/registerForRails';
import { CitationButton } from 'js/single_work/loswp/CitationButton';
import { init4gen } from 'js/scribd_api/4gen';
import { LoginModalManager } from 'js/login_modal/LoginModalManager';
import { setUpOutline } from 'js/single_work/loswp/setUpOutline';
import { setUpFloatingBadges } from 'js/single_work/loswp/setUpFloatingBadges';
import { StickyCtaFooter } from 'js/single_work/loswp/StickyCtaFooter';
import LoswpTabsHero from 'js/single_work/loswp/tabs_hero/LoswpTabsHero';
import LoswpPickDownloadHero from 'js/single_work/loswp/pick_download_hero/LoswpPickDownloadHero';
import LoswpPackPitch from 'js/single_work/loswp/preview_gate/LoswpPackPitch';
import { StickyWorkCardHeader } from 'js/single_work/loswp/StickyWorkCardHeader';
import { UngatedWorkCardDownloadCount } from 'js/single_work/loswp/UngatedWorkCardDownloadCount';
import { setUpInfoBadgeToggle } from 'js/single_work/loswp/setUpInfoBadgeToggle';
import { setUpAuthorPopup } from 'js/single_work/loswp/setUpAuthorPopup';
import { FeatureFeedback } from 'js/feature_feedback/FeatureFeedback';
import LoggedOutBucketingBar from 'js/logged_out_bucketing_bar/LoggedOutBucketingBar';
import AboutTheAuthorsSSR from 'js/liswp/sidebar_ds/AboutTheAuthorsSSR';
import { Attachment as BuildVuAttachment } from 'js/liswp/build_vu/Attachment';
import { GatedAttachment } from 'js/liswp/build_vu/GatedAttachment';

if (window.loswp.useOptimizedScribd4genScript) {
  init4gen();
}

import { trackCWV } from 'js/utils/trackCWV';
trackCWV(window.loswp.cwvAbTestBucket);

import { trackUrlFragments } from 'js/utils/trackUrlFragments';
trackUrlFragments();

// On some versions of LOSWP (drawbridge) the previewable
// attachments are retrieved server side.
setUpScribdDocument({
  work: window.loswp.work,
  previewableAttachments: window.loswp.previewableAttachments,
  hasViewableAttachments: window.loswp.hasViewableAttachments,
});

setUpHeaders();
setUpLoginButtons({ pageName: 'loswp' });
setUpClickTracking(window.loswp.work.id, window.loswp.designVariant);
setUpFigureCarousel();
setUpRelatedPapersCarousel();
setUpRelatedPapersCarouselCardClick();
setUpViewMoreRelatedWorks('related-papers', ['medium', 'large'], {
  initialShowCount: 3,
  showAllOnExpand: true,
});
setUpViewMoreRelatedWorks('related-work', ['large']);
setUpViewMoreRelatedWorks('cited-by', ['large']);
setUpViewMoreRelatedWorks('cited-by-v2', ['small', 'medium', 'large']);
setUpViewMoreRelatedWorks('cited-by-v2-related-work', [
  'small',
  'medium',
  'large',
]);
setUpTruncateComponent('abstract', 16, ['small', 'medium', 'large']);
setUpTruncateComponent('transcript', 4, ['small', 'medium', 'large']);
setUpOutline();

loadRecordHit().then(() => {
  // Don't start activity tracking until the hit is loaded, it needs the hit ID
  startActivityTracking();
});

const UNGATED_VARIANTS = ['ungated', 'ungated_v3', 'gated_blur_page_2'];
if (UNGATED_VARIANTS.includes(window.loswp.designVariant)) {
  setUpFloatingBadges();
  setUpInfoBadgeToggle();
  setUpAuthorPopup();
}

// _footer.html.slim -> application_helper.rb
if (window.loswp.shouldDetectTimezone) {
  set_gmtoffset(routes.gmtoffset_path({}));
}

// Confirmation banner, comes from show.html+fast.slim.
// Shown for new user accounts that haven't been confirmed yet
// or have been flagged as spam.
if (window.confirmAccountEmail) {
  const user = currentUser;
  const headerContainer = document.querySelector('#main-header-container');
  if (user && headerContainer?.parentElement) {
    const container = document.createElement('div');
    headerContainer.parentElement.insertBefore(
      container,
      headerContainer.nextSibling,
    );

    ReactDOM.render(
      <ConfirmationBanner email={window.confirmAccountEmail} user={user} />,
      container,
    );
  }
}

registerForRails({
  AboutTheAuthorsSSR,
  CitationButton,
  LoginModalManager,
  MoreActionsMenu,
  FeatureFeedback,
  LoggedOutBucketingBar,
  LoswpTabsHero,
  LoswpPickDownloadHero,
  LoswpPackPitch,
  // For SEO ungate
  BuildVuAttachment,
  GatedAttachment,
  StickyWorkCardHeader,
  StickyCtaFooter,
  UngatedWorkCardDownloadCount,
  // End SEO ungate components
});

// Watch impressions for all elements with the `data-impression-entity-id` attribute
watchDomImpressions();

import { startRecordingIfSamplingRequest, fsEvent } from 'js/fullstory';
startRecordingIfSamplingRequest();

Object.entries(window.loswp.abTestBuckets ?? {}).forEach(
  ([experimentName, variantName]) => {
    fsEvent('Experiment Viewed', {
      experiment_name: experimentName,
      variant_name: variantName,
    });
  },
);
